import React from 'react';
import type { SimpleHeaderComponent as SimpleHeaderComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';

interface SimpleHeaderComponentProps {
  component: SimpleHeaderComponentType;
}

export const SimpleHeaderComponent: React.FC<SimpleHeaderComponentProps> = ({ component }) => {
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Normalize data to ensure defaults are applied
  const rawData = component.data as Record<string, unknown>;
  const data = {
    ...component.data,
    alignment: (rawData.alignment as 'left' | 'center' | 'right') || 'center',
    size: (rawData.size as 'small' | 'medium' | 'large') || 'medium',
  };

  // Size classes for padding
  const sizeClasses = {
    small: 'py-8 md:py-12',
    medium: 'py-12 md:py-16',
    large: 'py-16 md:py-24',
  };

  // Title size classes based on component size
  const titleSizeClasses = {
    small: 'text-2xl md:text-3xl',
    medium: 'text-3xl md:text-4xl',
    large: 'text-4xl md:text-5xl',
  };

  // Alignment classes
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  // Container alignment for max-width content
  const containerAlignmentClasses = {
    left: '',
    center: 'mx-auto',
    right: 'ml-auto',
  };

  // Color priority: customColors (user explicit) > theme/brand package > stored data defaults
  const titleColor =
    data.customColors?.title || themeStyles.simpleHeader?.titleColor || data.textColor || '#111827';

  const subtitleColor =
    data.customColors?.subtitle ||
    themeStyles.simpleHeader?.subtitleColor ||
    data.textColor ||
    '#6b7280';

  // Helper to check if a string is a gradient
  const isGradient = (bg: string) =>
    bg?.startsWith('linear-gradient') || bg?.startsWith('radial-gradient');

  // Get background style
  const getBackgroundStyle = () => {
    const bgColor =
      data.customColors?.background ||
      themeStyles.simpleHeader?.background ||
      data.backgroundColor ||
      '#ffffff';

    if (isGradient(bgColor)) {
      return { backgroundImage: bgColor };
    }
    return { backgroundColor: bgColor };
  };

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.simpleHeader?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.simpleHeader?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Alignment classes for buttons
  const buttonAlignmentClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  // Render buttons helper
  const renderButtons = () => {
    if (!data.buttons || data.buttons.length === 0) return null;

    return (
      <div className={`flex flex-wrap gap-4 mt-6 ${buttonAlignmentClasses[data.alignment]}`}>
        {data.buttons.map((button, index) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  return (
    <section
      className={`${sizeClasses[data.size]} px-4 sm:px-6 lg:px-8`}
      style={getBackgroundStyle()}
    >
      <div className="max-w-7xl mx-auto">
        <div className={`max-w-4xl ${containerAlignmentClasses[data.alignment]}`}>
          {/* Title - Always H1 for semantic purposes on interior pages */}
          <h1
            className={`${titleSizeClasses[data.size]} font-bold leading-tight ${alignmentClasses[data.alignment]}`}
            style={{ color: titleColor, ...headingFontStyle }}
            dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
          />

          {/* Subtitle */}
          {data.subtitle && (
            <p
              className={`mt-4 text-lg md:text-xl leading-relaxed ${alignmentClasses[data.alignment]}`}
              style={{ color: subtitleColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
            />
          )}

          {/* Call-to-Action Buttons */}
          {renderButtons()}
        </div>
      </div>
    </section>
  );
};
